// B-162 — the pure half of "Cierre de conciliación" in Reportes: the client's close over a range of days,
// the one Nubia reports daily to Willinton instead of the large hand-typed workbook. The backend sums
// everything (`getCierreReconciliationReport`); this chooses the range, words it and lays out the Excel.

/** The longest range the backend reads in one report. */
export const MAX_DIAS = 92;

const hoyBogota = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });

const menosDias = (fecha, dias) => {
  const d = new Date(`${fecha}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - dias);
  return d.toISOString().slice(0, 10);
};

/** The range chips. "Ayer" first: the close is reported the day after. */
export const RANGOS = [
  { clave: "ayer", etiqueta: "Ayer" },
  { clave: "siete", etiqueta: "7 días" },
  { clave: "mes", etiqueta: "Este mes" },
  { clave: "anterior", etiqueta: "Mes anterior" },
];

/** `{ from, to }` for a chip, in Bogotá's calendar. */
export function rangoDeReporte(clave, hoy = hoyBogota()) {
  const ayer = menosDias(hoy, 1);
  if (clave === "siete") return { from: menosDias(ayer, 6), to: ayer };
  if (clave === "mes") return { from: `${hoy.slice(0, 7)}-01`, to: hoy };
  if (clave === "anterior") {
    const primero = new Date(`${hoy.slice(0, 7)}-01T00:00:00Z`);
    const ultimo = new Date(primero.getTime() - 86400000).toISOString().slice(0, 10);
    return { from: `${ultimo.slice(0, 7)}-01`, to: ultimo };
  }
  return { from: ayer, to: ayer };
}

/** Whether a typed range can be asked: both ends, in order, at most `MAX_DIAS` days. Its reason otherwise. */
export function problemaDeRango({ from, to } = {}) {
  if (!from || !to) return "Elige la fecha inicial y la final.";
  if (to < from) return "La fecha final es antes de la inicial.";
  const dias = Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1;
  if (dias > MAX_DIAS) return `El reporte admite hasta ${MAX_DIAS} días.`;
  return null;
}

/** "44 de 47" and the share, for a card. */
export const deTotal = (parte, total) => (total ? `${parte} de ${total} · ${Math.round((parte / total) * 100)}%` : "—");

/** A day's line in words, for the trend chart: open and attended alerts. */
export function puntosDeAlertas(dias) {
  return [...(dias || [])].reverse().map((d) => ({
    fecha: d.date, etiqueta: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`, abiertas: d.open, atendidas: d.attended,
  }));
}

/** What "Exportar Excel" writes: the days, the offices with most alerts, and the alerts by rule. */
export function hojasDeReporte(datos, nombreDeRegla = (clave) => clave) {
  const r = (dia) => dia.reconciliation || {};
  const vacio = (v) => (v === null || v === undefined ? "" : v);
  return [
    {
      nombre: "Cierre por día",
      titulo: `Cierre de conciliación ${datos.clientCode} del ${datos.period.from} al ${datos.period.to}`,
      columnas: [
        { titulo: "Fecha", ancho: 12 }, { titulo: "Oficinas", formato: "count" }, { titulo: "Cuadradas", formato: "count" },
        { titulo: "Por revisar", formato: "count" }, { titulo: "Incompletas", formato: "count" }, { titulo: "Alertas", formato: "count" },
        { titulo: "Abiertas", formato: "count" }, { titulo: "Atendidas", formato: "count" }, { titulo: "Oficinas con alerta", formato: "count" },
        { titulo: "Valor de las diferencias", formato: "money", ancho: 18 }, { titulo: "Σ saldo oficinas", formato: "money", ancho: 18 },
        { titulo: "Cuenta del corresponsal", formato: "money", ancho: 18 }, { titulo: "Diferencia", formato: "money", ancho: 16 },
      ],
      filas: (datos.days || []).map((d) => [
        d.date, d.offices, d.balanced, d.review, d.incomplete, d.alerts, d.open, d.attended, d.officesWithAlerts, d.alertsValue,
        vacio(r(d).totalOffices), vacio(r(d).account), vacio(r(d).difference),
      ]),
    },
    {
      nombre: "Oficinas con más alertas",
      columnas: [
        { titulo: "Código" }, { titulo: "Oficina", ancho: 24 }, { titulo: "Regional", ancho: 14 }, { titulo: "Alertas", formato: "count" },
        { titulo: "Abiertas", formato: "count" }, { titulo: "Días con alerta", formato: "count" }, { titulo: "Valor", formato: "money", ancho: 18 },
      ],
      filas: (datos.byOffice || []).map((o) => [o.officeCode, o.officeName, o.region, o.alerts, o.open, o.days, o.value]),
    },
    {
      nombre: "Alertas por tipo",
      columnas: [{ titulo: "Alerta", ancho: 34 }, { titulo: "Alertas", formato: "count" }, { titulo: "Abiertas", formato: "count" }, { titulo: "Valor", formato: "money", ancho: 18 }],
      filas: (datos.byRule || []).map((x) => [nombreDeRegla(x.ruleKey), x.alerts, x.open, x.value]),
    },
  ];
}
